

'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { NewProjectFormSchema } from '@/domains/projects/schemas/project-details';
import { createProject as createProjectOnServer } from '@/domains/projects/services/create-project';
import { useAuth } from '@/context/auth-provider';
import { Input } from '../../../components/ui/input';
import { BaseDialog } from '@/components/shared/base-dialog';

type FormValues = z.infer<typeof NewProjectFormSchema>;

interface NewProjectDialogProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  /** When provided, the newly created project will be added to this group automatically. */
  targetGroupId?: string | null;
  /** Callback to add the new project to the target group after creation. */
  onAddToGroup?: (projectId: string, groupId: string) => void;
}

export function NewProjectDialog({ isOpen, onOpenChange, targetGroupId, onAddToGroup }: NewProjectDialogProps) {
  const router = useRouter();
  const { user } = useAuth();
  const { toast } = useToast();
  const [isCreating, setIsCreating] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(NewProjectFormSchema),
    defaultValues: {
      title: '',
      blurb: '',
    },
  });

  async function onSubmit(values: FormValues) {
    if (!user) {
        toast({
            variant: 'destructive',
            title: 'Authentication Error',
            description: 'You must be logged in to create a project.',
        });
        return;
    }

    setIsCreating(true);
    try {
      const newProject = await createProjectOnServer(user.uid, values.title, values.blurb);

      // If a target group was specified, add the new project to it
      if (targetGroupId && onAddToGroup && newProject.id) {
        onAddToGroup(newProject.id, targetGroupId);
      }

      toast({
        title: "Project Created!",
        description: `"${values.title}" is ready for you.`,
      });

      onOpenChange(false);
      form.reset();
      // The parent page will now be responsible for re-fetching projects
      // router.push(`/projects/${newProject.id}`);

    } catch (error) {
      console.error('Failed to create project:', error);
      toast({
        variant: 'destructive',
        title: 'Creation Failed',
        description: 'Could not create the project. Please try again.',
      });
    } finally {
      setIsCreating(false);
    }
  }

  return (
    <BaseDialog
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title="Create New Project"
      description="Give your new story a title and a core blurb. You can generate the full architecture later."
      onSubmit={form.handleSubmit(onSubmit)}
      confirmLabel="Create Project"
      confirmLoadingLabel="Creating..."
      confirmLoading={isCreating}
      preventCloseOnOutsideClick
    >
      <Form {...form}>
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Project Title</FormLabel>
              <FormControl>
                <Input
                  placeholder="e.g., The Last Starlight"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="blurb"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Story Blurb</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="e.g., A detective in a futuristic city hunts a phantom that can manipulate memories."
                  className="min-h-[120px]"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </Form>
    </BaseDialog>
  );
}
